import type {GuessModelV1, DraftGuessModelV1} from '@albert-io/models';
import type {SetState, GetState} from './types';

const actions = (set: SetState, get: GetState) => ({
  addQuestionInfo: (question) => {
    // check if guess info exists
    const {guess, isSubmitted} = get();

    // If we're in "Show Solution" mode (isSubmitted=true but no real guess),
    // only update the question reference — don't reinitialize placements/wordBank.
    if (isSubmitted && !guess) {
      set((state) => {
        state.question = question;
      });
      return;
    }

    const attributes = question.getQuestionTypeAttributes().get('item-bank');
    const bankAttribute = attributes.get('bank');
    const itemsAttribute = attributes.get('slots');

    // capture info from submitted guess to pre-fill placements, and bank state
    let guessContent = {};
    let bankIdToSlotId = {};
    let correctAnswer = null;
    if (isSubmitted && guess && guess.getContent) {
      const validResponse = question.getValidResponse().toJS();
      correctAnswer = validResponse;
      guessContent = guess.getContent().toJS();
      Object.keys(guessContent).forEach((slotId) => {
        const bankId = guessContent[slotId];
        bankIdToSlotId[bankId] = slotId;
      });
    }

    const bank = bankAttribute.toJS().map((bankItem, index) => {
      return {
        id: bankItem.id || `word-${index}`,
        value: Array.isArray(bankItem.value) ? `[${bankItem.value}]` : bankItem.value,
        placedInSlot: bankIdToSlotId[bankItem.id] || null
      };
    });

    const dropSlots = itemsAttribute.toJS().map((slot, index) => ({
      id: slot.id || `slot-${index}`,
      label: Array.isArray(slot.value) ? `[${slot.value}]` : slot.value
    }));

    set((state) => {
      state.question = question;
      state.placements = guessContent;
      state.wordBank = bank;
      state.dropSlots = dropSlots;
      state.correctAnswer = correctAnswer;
      state.activeItemId = null;
      return state;
    });
  },

  addGuessInfo: (guess: GuessModelV1 | null) => {
    set((state) => {
      state.guess = guess;
      state.isSubmitted = !!guess;
    });
  },

  addDraftGuessInfo: (draftGuess: DraftGuessModelV1) => {
    const {question} = get();
    const hasQuestionInfo = question !== null && question.getQuestionTypeAttributes !== undefined;
    if (!hasQuestionInfo || !draftGuess) return;
    if (draftGuess?.getContent?.()) {
      set((state) => {
        const draftContentObject: {[slotId: string]: string} = draftGuess.getContent().toJS();
        state.wordBank.forEach((item) => {
          const updatedItem = item;
          const draftEntries = Object.entries(draftContentObject);
          if (draftEntries.some(([_key, value]) => value === item.id)) {
            const slotId = draftEntries.find(([_key, value]) => value === item.id)?.[0];
            updatedItem.placedInSlot = slotId || null;
          }
        });
        state.placements = draftContentObject;
        state.draftGuess = draftGuess;
      });
    }
  },
  // interaction actions
  setActiveItem: (itemId) => {
    set((state) => {
      state.activeItemId = itemId;
    });
  },
  placeItemInSlot: (itemId, slotId) => {
    set(
      (state) => {
        const movingItem = state.wordBank.find((item) => item.id === itemId);
        const movingItemCurrentSlot = movingItem?.placedInSlot;
        state.wordBank.forEach((item) => {
          // If this is the dragged item, update its placement
          const updatedItem = item;
          if (item.id === itemId) {
            updatedItem.placedInSlot = slotId === WORD_BANK_ID ? null : slotId;
            //  If another item was in the target slot, move it back to word bank
            //  If another item was in the moving item's original slot (for swaps), also send it to word bank
          } else if (item.placedInSlot === slotId || item.placedInSlot === movingItemCurrentSlot) {
            updatedItem.placedInSlot = null;
          }
          // update item in word bank
        });

        // Remove item from any other slot it might be in
        Object.keys(state.placements).forEach((key) => {
          if (state.placements[key] === itemId) {
            delete state.placements[key];
          }
        });

        // Place item in new slot
        if (slotId !== WORD_BANK_ID) {
          // if placing back to word bank, just remove from placements
          state.placements[slotId] = itemId;
        }
        state.activeItemId = null;
      },
      false,
      `placeItemInSlot-${itemId}-to-${slotId}`
    );
  },

  removeItemFromSlot: (slotId) => {
    set((state) => {
      delete state.placements[slotId];
    });
  },
  setIsDragging: (isDragging) => {
    set((state) => {
      state.isDragging = isDragging;
    });
  },
  // TODO: move to selectors or custom hook

  buildGuessContent: () => {
    const guessContent = get().placements;
    return guessContent;
  },

  // Computed helpers
  getItemInSlot: (slotId) => {
    return get().placements[slotId];
  },

  isSlotFilled: (slotId) => {
    return slotId in get().placements;
  },
  // TODO: update to use maps
  hasUserSelectedAnswer: () => {
    return Object.keys(get().placements).length > 0;
  },

  clearGuess: () => {
    set((state) => {
      state.guess = null;
      state.isSubmitted = false;
      state.correctAnswer = null;
      state.placements = {};
      state.activeItemId = null;
      state.wordBank.forEach((item) => {
        item.placedInSlot = null;
      });
    });
    setTimeout(() => {
      global.scrollTo(0, 0);
    }, 0);
  },

  showSolution: () => {
    const {question} = get();
    if (!question) return;

    let correctPlacements: Record<string, string> = {};
    try {
      correctPlacements = question.getValidResponse().toJS();
    } catch (_err) {
      return;
    }

    set((state) => {
      state.isSubmitted = true;
      state.correctAnswer = correctPlacements;
      state.placements = {...correctPlacements};
      state.wordBank.forEach((item) => {
        const slotId = Object.keys(correctPlacements).find(
          (key) => correctPlacements[key] === item.id
        );
        item.placedInSlot = slotId ?? null;
      });
    });
  }
});

export default actions;

/**
 *  STUBBING INFO
 *
 */

export const WORD_BANK_ID = 'word-bank';
